import { z } from "zod"
import { LanguageSchema } from "./language"
import { TranslationKey } from "./translation-key"
import { TranslationMessages } from "./translation-messages.generated"

// Messages nested by the dots in each key, the shape use-intl expects:
// "alfred.sidebar.calendar" → { alfred: { sidebar: { calendar: "…" } } }
export interface TranslationMessageTree {
  [segment: string]: string | TranslationMessageTree
}

export interface TranslationDictionary {
  // Latest modification across the language's rows; changes on every edit
  version: string
  messages: TranslationMessageTree
}

// What the apps receive: the same dictionary, typed with the generated keys so
// translators built from it (createTranslator) type-check their keys.
export interface TranslationDictionaryResponse {
  version: string
  messages: TranslationMessages
}

export interface AdminTranslationRow {
  key: string
  description: string | null
  sv: string | null
  en: string | null
  modifiedAt: string | null
  modifiedByName: string | null
}

export interface AdminTranslationList {
  rows: AdminTranslationRow[]
  totalCount: number
  namespaces: string[]
}

export const ADMIN_TRANSLATION_PAGE_SIZE = 200

export const AdminTranslationQuerySchema = z.object({
  search: z.string().trim().optional(),
  namespace: z.string().trim().optional(),
  missingLanguage: LanguageSchema.optional(),
})

export type AdminTranslationQuery = z.infer<typeof AdminTranslationQuerySchema>

export const TranslationChangeSchema = z.object({
  language: LanguageSchema,
  value: z
    .string()
    .trim()
    .min(1, "common.validation.textNotEmpty" satisfies TranslationKey),
})

export type TranslationChange = z.infer<typeof TranslationChangeSchema>

// All languages edited for one key, saved together so a renamed variable can
// be changed in every language at once
export const UpdateTranslationSchema = z.object({
  changes: z.array(TranslationChangeSchema).min(1),
})

export type UpdateTranslation = z.infer<typeof UpdateTranslationSchema>

export const UpdateUserLanguageSchema = z.object({
  language: LanguageSchema,
})

export type UpdateUserLanguage = z.infer<typeof UpdateUserLanguageSchema>
